import { useEffect, useRef, useState, type FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../auth/AuthProvider';
import { useCompanyFeatures } from '../hooks/useCompanyFeatures';
import { useChargeFlags } from '../hooks/useChargeFlags';
import { functions } from '../lib/firebase';
import { formatMoneyPence } from '../lib/format';
import { newIdempotencyKey } from '../lib/jobsBackend';
import { useJobFinanceStatus } from '../lib/jobFinance';
import { chargeDrafts, MAX_CHARGES, priceDraftPayload, type ChargeDraft } from '../lib/priceBreakdown';
import { canPriceJobs, type ChargeFlag, type Job } from '../types/models';
import { ReadFailure } from './ReadFailure';
import { Alert } from './ui/Alert';
import { Button } from './ui/Button';
import { Field, TextArea, TextInput, useFieldId } from './ui/Field';
import { Modal } from './ui/Modal';
import { Switch } from './ui/Switch';

export function JobPriceModal({ companyId, job, onClose }: {companyId: string; job: Job; onClose: () => void}) {
  const {user, activeCompanyId, activeRole} = useAuth();
  const finance = useJobFinanceStatus();
  const features = useCompanyFeatures();
  const flags = useChargeFlags(job.id, features.additionalChargesEnabled);
  const formId = useFieldId('job-price');
  const amountId = useFieldId('price-amount');
  const initialAmount = job.pricing ? (job.pricing.amountPence / 100).toFixed(2) : '';
  const [amount, setAmount] = useState(initialAmount);
  const [jobAmount, setJobAmount] = useState(job.pricing?.breakdown ? (job.pricing.breakdown.jobAmountPence / 100).toFixed(2) : initialAmount);
  const [lines, setLines] = useState(() => chargeDrafts(job.pricing));
  const [breakdown, setBreakdown] = useState(!!job.pricing?.breakdown?.charges.length);
  const [notes, setNotes] = useState(job.pricing?.notes ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  const scope = JSON.stringify([user?.uid, activeCompanyId, activeRole, companyId, job.id]);
  const initialScope = useRef(scope);
  const authorized = !!user && activeCompanyId === companyId && scope === initialScope.current && canPriceJobs(activeRole ?? undefined);
  const hiddenLines = features.ready && !features.additionalChargesEnabled && !!job.pricing?.breakdown?.charges.length;
  const allowed = authorized && finance.ready && features.ready && !hiddenLines && job.accountingStatus !== 'invoiced';
  let planned: ReturnType<typeof priceDraftPayload> | null = null;
  let validation: string | null = null;
  try { planned = priceDraftPayload(amount, breakdown, jobAmount, lines); }
  catch (e) { validation = (e as Error).message; }
  const shownAmount = breakdown ? planned ? (planned.amountPence / 100).toFixed(2) : '' : amount;
  if (!authorized) return null;

  function toggleBreakdown(next: boolean) {
    if (next) setJobAmount(amount);
    else { if (planned) setAmount((planned.amountPence / 100).toFixed(2)); setLines([]); }
    setBreakdown(next);
  }
  function changeLine(id: string, patch: Partial<ChargeDraft>) {
    setLines(current => current.map(line => line.id === id ? {...line, ...patch} : line));
  }
  function addLine(flag?: ChargeFlag) {
    if (busy || !allowed || !features.additionalChargesEnabled || lines.length >= MAX_CHARGES) return;
    if (!breakdown) {setJobAmount(amount); setBreakdown(true);}
    setLines(current => [...current, {id: newIdempotencyKey(), code: flag?.code ?? '', description: flag?.description ?? '',
      quantity: flag?.quantity == null ? '' : String(flag.quantity), amount: flag?.claimedAmountPence == null ? '' : (flag.claimedAmountPence / 100).toFixed(2), flagId: flag?.id}]);
  }
  async function dismiss(flag: ChargeFlag) {
    if (!allowed || busy) return;
    setBusy(flag.id); setError(null);
    try {
      await httpsCallable(functions, 'resolveChargeFlag')({companyId, jobId:job.id, flagId:flag.id, action:'dismiss'});
    } catch (e) {if(mounted.current) setError((e as Error).message);}
    finally {if(mounted.current) setBusy(null);}
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!allowed || busy) return;
    let price: ReturnType<typeof priceDraftPayload>;
    try {price = priceDraftPayload(amount, breakdown, jobAmount, lines);}
    catch(e) {setError((e as Error).message); return;}
    setBusy('save'); setError(null);
    try {
      await httpsCallable(functions, 'saveJobPricing')({companyId, jobId:job.id, ...price,
        currency:job.pricing?.currency || 'GBP', notes:notes.trim() || undefined});
      if(mounted.current) onClose();
    } catch(e) {if(mounted.current) setError((e as Error).message);}
    finally {if(mounted.current) setBusy(null);}
  }
  return <Modal open size="lg" title="Job price" description="Enter one overall price, or add an optional breakdown."
    onClose={onClose} dismissible={!busy} footer={<>
      <Button type="button" variant="secondary" onClick={onClose} disabled={!!busy}>Cancel</Button>
      <Button type="submit" form={formId} loading={busy === 'save'} disabled={!allowed || !!busy}>Save price</Button>
    </>}>
    <form id={formId} onSubmit={save} className="max-h-[60dvh] space-y-4 overflow-y-auto pr-1">
      {features.error && <ReadFailure message={features.error} retry={features.retry} />}
      {hiddenLines && <Alert tone="warning">This price has additional charges. Switch Additional charges on in Company settings before editing it.</Alert>}
      <Field label="Amount" htmlFor={amountId} hint={breakdown && features.additionalChargesEnabled ? 'Total from the breakdown below.' : undefined}>
        <div className="relative"><span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-xl text-ink3">£</span>
          <TextInput id={amountId} value={shownAmount} onChange={e => setAmount(e.target.value)} inputMode="decimal"
            readOnly={breakdown} disabled={!allowed || !!busy} autoFocus placeholder="0.00" className="h-14 pl-8 font-mono text-2xl" />
        </div>
      </Field>
      {features.additionalChargesEnabled && <>
        <div className="flex items-center justify-between gap-3"><h3 className="font-semibold text-ink">Breakdown (optional)</h3>
          <Switch label="Include breakdown" checked={breakdown} onChange={toggleBreakdown} disabled={!allowed || !!busy} />
        </div>
        {breakdown && <div className="space-y-3">
          <Field label="Job price"><TextInput inputMode="decimal" value={jobAmount} onChange={e => setJobAmount(e.target.value)} disabled={!allowed || !!busy} /></Field>
          {lines.map((line, index) => <fieldset key={line.id} aria-label={`Additional charge ${index + 1}`} className="space-y-3 rounded-[10px] border border-hair p-3">
            <legend className="px-1 text-sm font-semibold">Additional charge {index + 1}</legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Code" optional><TextInput value={line.code} maxLength={8} onChange={e => changeLine(line.id,{code:e.target.value.toUpperCase()})} disabled={!allowed || !!busy} /></Field>
              <Field label="Quantity" optional><TextInput value={line.quantity} inputMode="decimal" onChange={e => changeLine(line.id,{quantity:e.target.value})} disabled={!allowed || !!busy} /></Field>
              <Field label="Description" optional><TextInput value={line.description} maxLength={200} onChange={e => changeLine(line.id,{description:e.target.value})} disabled={!allowed || !!busy} /></Field>
              <Field label="Charge amount"><TextInput value={line.amount} inputMode="decimal" onChange={e => changeLine(line.id,{amount:e.target.value})} disabled={!allowed || !!busy} /></Field>
            </div>
            <Button type="button" variant="ghost" size="sm" disabled={!allowed || !!busy} onClick={() => setLines(current => current.filter(item => item.id !== line.id))}>Remove charge {index + 1}</Button>
          </fieldset>)}
        </div>}
        <Button type="button" variant="secondary" size="sm" disabled={!allowed || !!busy || lines.length >= MAX_CHARGES} onClick={() => addLine()}>＋ Add a charge</Button>
        {validation && breakdown && <p className="text-sm text-st-cancelled">{validation}</p>}
        <div className="space-y-2 border-t border-rowhair pt-3"><h3 className="font-semibold text-ink">Driver flagged</h3>
          {flags.error ? <ReadFailure message={flags.error} retry={flags.retry} /> : flags.loading ? <p className="text-sm text-ink3">Loading charge flags…</p>
            : flags.flags.filter(flag => flag.status === 'open').map(flag => <div key={flag.id} className="space-y-2 rounded-[10px] bg-surface p-3 text-sm">
              <p className="break-words font-semibold">{[flag.code, flag.description].filter(Boolean).join(' · ')}{flag.quantity == null ? '' : ` (${flag.quantity})`}</p>
              {flag.claimedAmountPence != null && <p>Claimed {formatMoneyPence(flag.claimedAmountPence,job.pricing?.currency || 'GBP')}</p>}
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="secondary" disabled={!allowed || !!busy || lines.length >= MAX_CHARGES || lines.some(line => line.flagId === flag.id)} onClick={() => addLine(flag)}>Price it</Button>
                <Button type="button" size="sm" variant="ghost" disabled={!allowed || !!busy || lines.some(line => line.flagId === flag.id)} onClick={() => void dismiss(flag)}>Dismiss</Button>
              </div>
            </div>)}
          {!flags.loading && !flags.error && flags.flags.every(flag => flag.status !== 'open') && <p className="text-sm text-ink3">No open charge flags.</p>}
        </div>
      </>}
      <Field label="Notes" optional><TextArea value={notes} onChange={e => setNotes(e.target.value)} rows={3} disabled={!allowed || !!busy} /></Field>
      {error && <Alert tone="error">{error}</Alert>}
    </form>
  </Modal>;
}
